// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React from 'react';
import { UseCase } from '@tls/sw91-communication/types/com.base';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { colorIconSelector, colorKeySelector, colorLabelSelector } from 'components/common/ColorOptions';
import { LabelInputProps } from 'components/projectSelection/common/ProjectSettingTypes';
import useAppPermissions from 'hooks/useAppPermissions';
import { Button, DropdownContent, InputFieldText } from '@tls/treact-ui';
import { IconPlus } from '@tls/treact-ui/icons';

const LabelInput = ({
  useCase,
  containerRef,
  labelsInfo,
  addLabel,
  updateLabel,
  removeLabel,
  availableColorOptions,
  isEditing = false,
  isDuplicating = false,
}: Readonly<LabelInputProps>) => {
  const { t } = useTranslation();

  const { hasMultiLabelCDPermision } = useAppPermissions();

  const supportsMultipleLabels =
    useCase === UseCase.QUALITY_CHECK || (useCase === UseCase.COMPONENT_DETECTION && hasMultiLabelCDPermision);
  const showLabelDelete = !isDuplicating && (isEditing || supportsMultipleLabels);
  const showLabelAdd = !isDuplicating && supportsMultipleLabels;
  const labelsToShow = labelsInfo.slice(1);

  return (
    <>
      {labelsToShow.map((label, index) => (
        <InputColumns key={label.labelColor.id} className='match-header'>
          <InputFieldText
            flag='mandatory'
            label={t('project.settings.label.name')}
            value={label.labelName}
            onValueChange={v => updateLabel(index + 1, { ...label, labelName: v })}
            asAtom
            data-testid={`project-settings-label-${index + 1}-name-input`}
          />
          <DropdownContent
            label={t('project.settings.label.color')}
            className='no-shadow'
            options={availableColorOptions}
            selectedOption={label.labelColor}
            keySelector={colorKeySelector}
            labelSelector={colorLabelSelector(t)}
            iconSelector={colorIconSelector}
            onChange={v => updateLabel(index + 1, { ...label, labelColor: v })}
            containerRef={containerRef as React.RefObject<Element>}
            collapse
            data-testid={`project-settings-label-${index + 1}-color-dropdown`}
            dataTestIdSelector={option =>
              `project-settings-label-${index + 1}-color-option-${String(colorKeySelector(option))}`
            }
          />
          {showLabelDelete && (
            <DeleteButton>
              <Button
                as='button'
                iconKey='IconDelete'
                onClick={() => removeLabel(index + 1)}
                variant='secondary'
                disabled={labelsToShow.length <= 1}
                data-testid={`project-settings-label-${index + 1}-delete-button`}
              />
            </DeleteButton>
          )}
        </InputColumns>
      ))}
      {showLabelAdd && (
        <RightButton>
          <Button
            variant='secondary'
            icon={<IconPlus />}
            label={t('project.settings.label.add')}
            onClick={addLabel}
            disabled={labelsToShow.length >= 5}
            data-testid='project-settings-label-add-button'
          />
        </RightButton>
      )}
    </>
  );
};

LabelInput.displayName = 'LabelInput';

const InputColumns = styled.div`
  display: flex;
  grid-gap: 8px;
`;

const DeleteButton = styled.div`
  padding-top: 1.5rem;
  display: grid;
  place-items: end;
`;

const RightButton = styled.div`
  display: grid;
  grid-gap: 8px;
  place-items: end;
  margin-right: 0.5rem;
`;
export default LabelInput;
